import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import dayjs from 'dayjs';
import { Box, Typography, TextField, FormLabel, Grid, MenuItem, Collapse, Divider, useTheme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import Question from "../../components/Question";
import ContinueButton from "../../components/ContinueButton";
import ImageUpload from "../../components/ImageUpload";
import {
    setInsurance,
    setCardFront,
    setCardBack,
} from '../../redux/insuranceSlice';
import relationships from '../../assets/insure-relations.json';
import insurers from '../../assets/insurers.json';
import useImageDownload from '../../hooks/useImageDownload';
import { continueButtonSx, inputLabelSx, textFieldSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

export default function ConfirmInsurance() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const theme = useTheme();

    const { returning = false, insuranceChanged = false, onFile = false } = location.state || {};
    const insurance = useSelector((state) => state.insurance);
    const user = useSelector((state) => state.user);

    const [insurer, setInsurer] = useState(insurance.insurer || "");
    const [insurerError, setInsurerError] = useState(false);
    const [id, setId] = useState(insurance.memberID || "");
    const [idError, setIdError] = useState(false);
    const [relat, setRelat] = useState(insurance.policyOwner?.relationship || "");
    const [relError, setRelError] = useState(false);

    const [ownerFname, setOwnerFname] = useState(insurance.policyOwner?.fname || "");
    const [ownerFnameError, setOwnerFnameError] = useState(false);
    const [ownerLname, setOwnerLname] = useState(insurance.policyOwner?.lname || "");
    const [ownerLnameError, setOwnerLnameError] = useState(false);
    const [ownerGender, setOwnerGender] = useState(insurance.policyOwner?.gender || "");
    const [ownerGenderError, setOwnerGenderError] = useState(false);
    const [ownerDOB, setOwnerDOB] = useState(insurance.policyOwner?.dob ? dayjs(insurance.policyOwner.dob) : null);
    const [ownerDOBError, setOwnerDOBError] = useState(false);

    const frontCardFile = useImageDownload(insurance.cardFront && insurance.cardFront[0]);
    const backCardFile = useImageDownload(insurance.cardBack && insurance.cardBack[0]);

    const handleCardFrontUpload = (files) => {
        dispatch(setCardFront(files));
    };

    const handleCardBackUpload = (files) => {
        dispatch(setCardBack(files));
    };

    const genderOpts = ["Female", "Male", "Other", "Unknown"];
    const dobErrorMessage = {
        invalidDate: "Please enter a valid date.",
        minDate: "Please enter a valid date.",
        disableFuture: "Please enter a date in the past.",
        required: "This field is required.",
    };

    useEffect(() => {
        if (returning) {
            setInsurer(insurance.insurer || "");
            setId(insurance.memberID || "");
            setRelat(insurance.policyOwner?.relationship || "");
            setOwnerFname(insurance.policyOwner?.fname || "");
            setOwnerLname(insurance.policyOwner?.lname || "");
            setOwnerGender(insurance.policyOwner?.gender || "");
            setOwnerDOB(insurance.policyOwner?.dob ? dayjs(insurance.policyOwner.dob) : null);
            setCardFront(frontCardFile ? [frontCardFile] : insurance.cardFront);
            setCardBack(backCardFile ? [backCardFile] : insurance.cardBack);
        }
    }, [returning, insurance, frontCardFile, backCardFile]);

    const isFormValid = () => {
        const isMemberIdValid = (insurance.cardFront && insurance.cardFront[0]) ? true : id;
        return insurer && isMemberIdValid && relat &&
            (relat === "Self" || (ownerFname && ownerLname && ownerGender && ownerDOB && !ownerDOBError));
    };

    const handleSubmit = () => {
        dispatch(setInsurance({
            ...insurance,
            insurer: insurer,
            memberID: id,
            policyOwner: {
                relationship: relat,
                fname: ownerFname,
                lname: ownerLname,
                gender: ownerGender,
                dob: ownerDOB ? ownerDOB.toISOString() : null,
            },
            cardFront: frontCardFile ? [frontCardFile] : insurance.cardFront,
            cardBack: backCardFile ? [backCardFile] : insurance.cardBack,
        }));
        setPageAndDeferNavigate(dispatch, '/anything-else-toknow', () =>
            navigate('/anything-else-toknow', { state: { returning: true, insuranceChanged, onFile } })
        );
    };

    const handleBack = () => {
        navigate('/insurance/provider', { state: { returning: true } });
    };

    return (
        <Question
            onBack={handleBack}
            title="Thanks! Could you confirm your insurance details, please?"
            subtitle={user.missingPatient ? "" : "If your insurance has changed, you can update it below."}
            returning={returning}
            input={
                <Box sx={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
                    <FormLabel id="details-label">
                        <Typography variant="h6" sx={{ my: "1rem" }}>
                            Insurance Details
                        </Typography>
                    </FormLabel>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Insurance Provider"
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    ...textFieldSx, width: "100%",
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                                InputLabelProps={{
                                    sx: {
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    }
                                }}
                                value={insurer}
                                onChange={(e) => {
                                    setInsurerError(e.target.value === "");
                                    setInsurer(e.target.value);
                                }}
                                required
                                select
                                error={insurerError}
                                helperText={insurerError ? "This field is required." : ""}
                            >
                                {user.address.state ? (
                                    insurers[user.address.state].map((provider) => (
                                        <MenuItem key={provider} value={provider}>
                                            {provider}
                                        </MenuItem>
                                    ))
                                ) : (
                                    <Typography sx={{ mt: "1rem" }}>
                                        Please return to the address page and enter your state first.
                                    </Typography>
                                )}
                            </TextField>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Member ID"
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    ...textFieldSx, width: "100%",
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                                InputLabelProps={{
                                    sx: {
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    }
                                }}
                                autoComplete="off"
                                value={id}
                                onChange={(e) => {
                                    setIdError(e.target.value === "" && (!insurance.cardFront || !insurance.cardFront[0]));
                                    setId(e.target.value);
                                }}
                                required={(!insurance.cardFront || !insurance.cardFront[0])}
                                error={idError}
                                helperText={idError ? "This field is required." : ""}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Relationship to Policy Owner"
                                variant="outlined"
                                color="secondary"
                                sx={{
                                    ...textFieldSx, width: "100%",
                                    "& .MuiInputLabel-root": {
                                        backgroundColor: theme.palette.background.paper,
                                        padding: "0 32px",
                                        transition: "all 0.2s ease-in-out",
                                    },
                                }}
                                InputLabelProps={{
                                    sx: {
                                        ...inputLabelSx,
                                        "& .MuiInputLabel-shrink": {
                                            backgroundColor: theme.palette.background.paper,
                                        },
                                    }
                                }}
                                value={relat}
                                onChange={(e) => {
                                    setRelError(e.target.value === "");
                                    setRelat(e.target.value);
                                    if (e.target.value === "Self") {
                                        setOwnerFname("");
                                        setOwnerLname("");
                                        setOwnerGender("");
                                        setOwnerDOB(null);
                                    }
                                }}
                                required
                                select
                                error={relError}
                                helperText={relError ? "This field is required." : ""}
                            >
                                {relationships.relations.map((relation) => (
                                    <MenuItem key={relation} value={relation}>
                                        {relation}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>

                    <Collapse in={relat && relat !== "Self"} mountOnEnter unmountOnExit>
                        <Box sx={{ width: "100%" }}>
                            <FormLabel id="policy-owner-label">
                                <Typography variant="h6" sx={{ my: "1rem" }}>
                                    Policy Owner Details
                                </Typography>
                            </FormLabel>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <TextField
                                        label="First Name"
                                        variant="outlined"
                                        color="secondary"
                                        sx={{
                                            ...textFieldSx, width: "100%",
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            }
                                        }}
                                        autoComplete="off"
                                        value={ownerFname}
                                        onChange={(e) => {
                                            setOwnerFnameError(e.target.value === "");
                                            setOwnerFname(e.target.value);
                                        }}
                                        required
                                        error={ownerFnameError}
                                        helperText={ownerFnameError ? "This field is required." : ""}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Last Name"
                                        variant="outlined"
                                        color="secondary"
                                        sx={{
                                            ...textFieldSx, width: "100%",
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            }
                                        }}
                                        autoComplete="off"
                                        value={ownerLname}
                                        onChange={(e) => {
                                            setOwnerLnameError(e.target.value === "");
                                            setOwnerLname(e.target.value);
                                        }}
                                        required
                                        error={ownerLnameError}
                                        helperText={ownerLnameError ? "This field is required." : ""}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField
                                        label="Gender"
                                        color="secondary"
                                        sx={{
                                            ...textFieldSx, width: "100%",
                                            "& .MuiInputLabel-root": {
                                                backgroundColor: theme.palette.background.paper,
                                                padding: "0 32px",
                                                transition: "all 0.2s ease-in-out",
                                            },
                                        }}
                                        InputLabelProps={{
                                            sx: {
                                                ...inputLabelSx,
                                                "& .MuiInputLabel-shrink": {
                                                    backgroundColor: theme.palette.background.paper,
                                                },
                                            }
                                        }}
                                        value={ownerGender}
                                        onChange={(e) => {
                                            setOwnerGenderError(e.target.value === "");
                                            setOwnerGender(e.target.value);
                                        }}
                                        required
                                        select
                                        error={ownerGenderError}
                                        helperText={ownerGenderError ? "This field is required." : ""}
                                    >
                                        {genderOpts.map((opt) => (
                                            <MenuItem key={opt} value={opt}>
                                                {opt}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={6}>
                                    <DatePicker
                                        label="Date of Birth"
                                        id="dob"
                                        slotProps={{
                                            textField: {
                                                sx: {
                                                    ...textFieldSx,
                                                    width: '100%',
                                                    mb: '2rem',
                                                    "& .MuiInputLabel-root": {
                                                        backgroundColor: theme.palette.background.paper,
                                                        padding: "0 32px",
                                                        transition: "all 0.2s ease-in-out",
                                                    },
                                                },
                                                InputLabelProps: {
                                                    sx: {
                                                        ...inputLabelSx,
                                                        "& .MuiInputLabel-shrink": {
                                                            backgroundColor: theme.palette.background.paper,
                                                        },
                                                    }
                                                },
                                                helperText: ownerDOBError ? dobErrorMessage[ownerDOBError] : "",
                                                error: !!ownerDOBError
                                            }
                                        }}
                                        onChange={(date) => {
                                            setOwnerDOB(date);
                                        }}
                                        value={ownerDOB}
                                        onError={(err) => {
                                            if (ownerDOB) {
                                                setOwnerDOBError(err);
                                            }
                                        }}
                                        disableFuture
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Collapse>

                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 3 }}>
                        <Divider sx={{ flex: 1 }} />
                        <Typography variant="h7" color="secondary" sx={{ px: 1, fontWeight: "normal" }}>
                            Optional
                        </Typography>
                        <Divider sx={{ flex: 1 }} />
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <FormLabel id="front-card-label">
                                <Typography variant="h6" sx={{ mb: "1rem", mt: "0.5rem" }}>
                                    Insurance Card Front
                                </Typography>
                            </FormLabel>
                            <ImageUpload
                                multiple={false}
                                // TBD - show user non editable insurance data if user confirms insurance has changed take them to a new insurance form page rather than editable one 
                                files={returning && frontCardFile ? [frontCardFile] : insurance.cardFront}
                                setFiles={handleCardFrontUpload}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormLabel id="back-card-label">
                                <Typography variant="h6" sx={{ mb: "1rem", mt: "0.5rem" }}>
                                    Insurance Card Back
                                </Typography>
                            </FormLabel>
                            <ImageUpload
                                multiple={false}
                                // TBD - show user non editable insurance data if user confirms insurance has changed take them to a new insurance form page rather than editable one
                                files={returning && backCardFile ? [backCardFile] : insurance.cardBack}
                                setFiles={handleCardBackUpload}
                            />
                        </Grid>
                    </Grid>
                </Box>
            }
            footer={
                <Box
                    sx={{
                        ...continueButtonSx,
                        width: '100%',
                        maxWidth: '900px',
                        margin: '0 auto',
                        position: "sticky",
                        bottom: 0,
                    }}
                >
                    <ContinueButton
                        onClick={handleSubmit}
                        title="Continue"
                        disabled={!isFormValid()}
                        selected={isFormValid()}
                    />
                </Box>
            }
        />
    );
}
